import { useState, useEffect } from 'react';
import { PhotoIcon, ClockIcon } from '@heroicons/react/24/outline';
import Cookies from 'js-cookie';

const ImagePreview = ({ batchId, image, altText }) => {
  const [status, setStatus] = useState('loading');
  const [blobUrl, setBlobUrl] = useState(null);
  const [debugInfo, setDebugInfo] = useState('');

  useEffect(() => {
    const loadImage = async () => {
      try {
        setStatus('loading');
        
        const token = Cookies.get('auth_token');
        if (!token) {
          setStatus('error');
          setDebugInfo('No authentication token found');
          return;
        }

        // Fetch image with proper auth headers
        const response = await fetch(`/api/batches/${batchId}/images/${image.id}/preview`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        // Convert to blob and create blob URL
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);
        setStatus('loaded');
        setDebugInfo('Image loaded successfully');

      } catch (error) {
        console.error('❌ Image load error:', error);
        setStatus('error');
        setDebugInfo(`Failed to load: ${error.message}`);
      }
    };

    loadImage();

    // Cleanup blob URL when component unmounts or image changes
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [batchId, image.id]);

  // Cleanup blob URL when component unmounts
  useEffect(() => {
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [blobUrl]);

  if (status === 'loading') {
    return (
      <div className="flex items-center justify-center w-full h-full bg-gray-200">
        <div className="text-center">
          <div className="w-4 h-4 mx-auto mb-1 loading-spinner"></div>
          <span className="text-xs text-gray-500">Loading...</span>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="flex items-center justify-center w-full h-full bg-gray-200">
        <div className="text-center">
          <PhotoIcon className="w-8 h-8 mx-auto mb-1 text-gray-400" />
          <span className="text-xs text-gray-500">Error</span>
          <div className="mt-1 text-xs text-red-500">{debugInfo}</div>
        </div>
      </div>
    );
  }

  // Show the image using blob URL (secure)
  return (
    <div className="flex items-center justify-center w-full h-full bg-gray-200">
      <img
        src={blobUrl}
        alt={altText}
        className="object-cover w-full h-full"
        onLoad={() => console.log('✅ Image loaded from blob URL')}
        onError={(e) => {
          console.error('❌ Blob image load error:', e);
          setStatus('error');
          setDebugInfo('Failed to display image');
        }}
      />
    </div>
  );
};

export const ProcessingImagePlaceholder = ({ status = 'processing' }) => {
  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'Pending...';
      case 'queued': return 'Queued...';
      case 'processing': return 'Processing...';
      case 'reprocessing': return 'Reprocessing...';
      default: return 'Processing...';
    }
  };

  return (
    <div className="flex items-center justify-center w-full h-full bg-gray-200">
      <div className="text-center">
        <ClockIcon className="w-6 h-6 mx-auto mb-1 text-gray-400" />
        <span className="text-xs text-gray-500">{getStatusText(status)}</span>
      </div>
    </div>
  );
};

export default ImagePreview;